import {
  OBSTACLE_LIMIT_MAX,
  OBSTACLE_LIMIT_MIN,
  ENEMY_SPAWN_MAX,
  TEAMMATE_SPAWN_MAX,
} from '../../../../game';
import { isFeatureEnabled } from '../../../../shared/config';
import { SpawnControlRow } from '../../../../shared/ui';
import type { SettingsGameToolsProps } from '../../types';
import './styles.css';

/** Pure UI: Settings → Game tools (props-driven). */
export function GameTabInterface(props: SettingsGameToolsProps) {
  const {
    mode,
    enemyAlive,
    enemySpawnCount,
    teammateAlive,
    teammateSpawnCount,
    immortal,
    blockAutoAim,
    obstaclesEnabled,
    obstacleLimit,
    obstacleLimitLabelRef,
    onEnemySpawnCount,
    onTeammateSpawnCount,
    onSpawnEnemy,
    onSpawnTeammate,
    onToggleImmortal,
    onToggleBlockAutoAim,
    onToggleObstacles,
    onObstacleLimit,
    getObstacleCount,
  } = props;

  const showEnemy = isFeatureEnabled('enemySpawn', mode);
  const showTeammate = isFeatureEnabled('teammateSpawn', mode);
  const showImmortal = isFeatureEnabled('immortal', mode);
  const showObstacles = isFeatureEnabled('obstacleControls', mode);

  return (
    <div className="settings-game-tools">
      <button
        type="button"
        className={
          blockAutoAim
            ? 'arcade-auto-aim arcade-auto-aim--on settings-action-btn'
            : 'arcade-auto-aim arcade-auto-aim--off settings-action-btn'
        }
        onClick={onToggleBlockAutoAim}
        aria-pressed={blockAutoAim}
        title={
          blockAutoAim
            ? 'Missiles auto-aim at blocks (green HUD). Click to disable.'
            : 'Block auto-aim off — silver HUD, straight shots. Click to enable.'
        }
      >
        {blockAutoAim ? 'AUTO-AIM ON' : 'AUTO-AIM OFF'}
      </button>

      {showEnemy ? (
        <SpawnControlRow
          kind="enemy"
          rowClassName="settings-tool-row settings-spawn-row"
          count={enemySpawnCount}
          max={ENEMY_SPAWN_MAX}
          unitAlive={enemyAlive}
          onCountChange={onEnemySpawnCount}
          onSpawn={onSpawnEnemy}
          countClassName="settings-count"
          actionClassName="settings-action-btn"
        />
      ) : null}

      {showTeammate ? (
        <SpawnControlRow
          kind="teammate"
          rowClassName="settings-tool-row settings-spawn-row"
          count={teammateSpawnCount}
          max={TEAMMATE_SPAWN_MAX}
          unitAlive={teammateAlive}
          onCountChange={onTeammateSpawnCount}
          onSpawn={onSpawnTeammate}
          countClassName="settings-count"
          actionClassName="settings-action-btn"
        />
      ) : null}

      {showImmortal ? (
        <button
          type="button"
          className={
            immortal
              ? 'arcade-immortal arcade-immortal--on settings-action-btn'
              : 'arcade-immortal settings-action-btn'
          }
          onClick={onToggleImmortal}
        >
          {immortal ? 'IMMORTAL ON' : 'IMMORTAL'}
        </button>
      ) : null}

      {showObstacles ? (
        <>
          <button
            type="button"
            className="arcade-stop-obstacles settings-action-btn"
            onClick={onToggleObstacles}
          >
            {obstaclesEnabled ? 'STOP OBSTACLES' : 'START OBSTACLES'}
          </button>
          <label className="arcade-obstacle-limit settings-obstacle-limit">
            <span className="arcade-obstacle-limit__label" ref={obstacleLimitLabelRef}>
              BLOCKS 0/{obstacleLimit}
            </span>
            <input
              type="range"
              className="arcade-obstacle-limit__slider"
              min={OBSTACLE_LIMIT_MIN}
              max={OBSTACLE_LIMIT_MAX}
              step={1}
              value={obstacleLimit}
              aria-label="Maximum obstacle blocks"
              onChange={(event) => {
                const next = Number(event.target.value);
                onObstacleLimit(next);
                const label = obstacleLimitLabelRef.current;
                if (label) {
                  label.textContent = `BLOCKS ${getObstacleCount()}/${next}`;
                }
              }}
            />
          </label>
        </>
      ) : null}
    </div>
  );
}
